import React, { useMemo } from "react";
import ExcelDownloader from "../../common/ExcelDownloader/ExcelDownloader";
import {
  getDisplayReportingAccountName,
  getSimpleFormattedDate,
} from "../../../utils/utils";
import {
  ADVANCED_NII_ACCOUNT_NAMES,
  ADVANCED_NII_PROPERTY_TYPES,
} from "../../../utils/constants";

const getAccountNodeByPropertyType = (datas, propertyType) => {
  const filteredPropertyType = datas?.find(
    (data) => data.propertyType === propertyType,
  );
  return filteredPropertyType?.accountNode || [];
};

const flattenForExport = (
  nodes,
  level = 0,
  rows = [],
  excludeReportingAccountNames = [],
) => {
  (nodes || []).forEach((node) => {
    const displayName =
      node?.formattedReportingAccountName || node?.reportingAccountName;

    if (excludeReportingAccountNames.includes(displayName)) {
      return;
    }

    rows.push({
      level,
      accountNum: node.accountNum,
      reportingAccountName: getDisplayReportingAccountName(
        node.reportingAccountName,
      ),
      formattedReportingAccountName: getDisplayReportingAccountName(
        node.formattedReportingAccountName || node.reportingAccountName,
      ),
      ...node.datas,
    });

    if (node.subAccounts?.length) {
      flattenForExport(
        node.subAccounts,
        level + 1,
        rows,
        excludeReportingAccountNames,
      );
    }
  });

  return rows;
};

const normalizeSummaryRows = (summaryRows) =>
  (summaryRows || []).map((row) => ({
    name: row.name,
    value: row.value,
  }));

const toSheetName = (label) =>
  label
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_]/g, "")
    .toUpperCase();

const buildTotalRow = (tabNodes, summaryLabel) => {
  const receivablesRow = tabNodes?.find(
    (row) =>
      row.reportingAccountName === ADVANCED_NII_ACCOUNT_NAMES.TOTAL_RECEIVABLES,
  );

  const payablesRow = tabNodes?.find(
    (row) =>
      row.reportingAccountName === ADVANCED_NII_ACCOUNT_NAMES.TOTAL_PAYABLES,
  );

  if (!receivablesRow || !payablesRow) return null;

  const allKeys = new Set([
    ...Object.keys(receivablesRow.datas || {}),
    ...Object.keys(payablesRow.datas || {}),
  ]);

  const totalDatas = {};
  allKeys.forEach((key) => {
    totalDatas[key] =
      Number(receivablesRow.datas?.[key] || 0) +
      Number(payablesRow.datas?.[key] || 0);
  });

  return {
    level: 0,
    accountNum: null,
    reportingAccountName: summaryLabel,
    formattedReportingAccountName: summaryLabel,
    ...totalDatas,
    datas: totalDatas,
  };
};

const AdvancedNIISensitivityModelerExport = ({
  advancedNIIResultData = [],
  summaryDataByTab = {},
  tabConfig = [],
  disabled = true,
}) => {
  const exportSheets = useMemo(() => {
    const sheets = [];

    tabConfig.forEach((tab) => {
      const tabNodes = getAccountNodeByPropertyType(
        advancedNIIResultData,
        tab.propertyType,
      );

      const excludeNames =
        tab.propertyType === ADVANCED_NII_PROPERTY_TYPES.BALANCE
          ? [ADVANCED_NII_ACCOUNT_NAMES.LATEST_ANNUAL_NII_FORECAST]
          : [];

      const detailRows = flattenForExport(tabNodes, 0, [], excludeNames);

      const totalRow = buildTotalRow(tabNodes, tab.summaryLabel);
      if (totalRow) {
        detailRows.push(totalRow);
      }

      const detailHeaders = [
        {
          key: "formattedReportingAccountName",
          label: "Reporting Account Name",
        },
        ...Object.keys(detailRows?.[0] || {})
          .filter(
            (key) =>
              ![
                "level",
                "accountNum",
                "reportingAccountName",
                "formattedReportingAccountName",
                "datas",
              ].includes(key),
          )
          .map((key) => ({
            key,
            label: key,
          })),
      ];

      sheets.push({
        name: `${toSheetName(tab.label)}_DETAIL`,
        header: detailHeaders,
        data: detailRows,
        showHeader: true,
      });

      if (tab.propertyType !== ADVANCED_NII_PROPERTY_TYPES.BALANCE) {
        sheets.push({
          name: `${toSheetName(tab.label)}_SUMMARY`,
          title: tab.summaryLabel,
          header: [
            { key: "name", label: "Name" },
            { key: "value", label: "Value" },
          ],
          data: normalizeSummaryRows(summaryDataByTab[tab.propertyType] || []),
          showHeader: false,
        });
      }
    });

    return sheets;
  }, [advancedNIIResultData, summaryDataByTab, tabConfig]);

  return (
    <ExcelDownloader
      sheets={exportSheets}
      fileName={`AdvancedNIISensitivityModeler_${getSimpleFormattedDate()}.xlsx`}
      disabled={disabled}
    />
  );
};

export default AdvancedNIISensitivityModelerExport;
